import { useEffect, useMemo, useState } from 'react'
import { useLocalization } from '../../Hooks/useLocalization'
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner'
import './TableList.scss'
import { sortResultString } from '../../Utils/Arrays'
import { FaIcon } from '../FaIcon/FaIcon'

interface configuration {
  show: boolean
  label: string
  getValue?: (value: unknown, key: string) => string
  getElementValue?: (value: unknown, key: string) => JSX.Element
}

interface TableListProps<T> {
  items: T[]
  configurations: {
    [property in keyof T]: configuration
  }
  localizationSection: string
  getUniqueKey: (t: T) => string
  onRowClick?: (t: T) => void
  isLoading: boolean
}

const baseDisplayLimit = 50
declare type SortDirection = 'none' | 'ascending' | 'descending'

export function TableList<T>({
  items,
  configurations,
  localizationSection,
  getUniqueKey,
  onRowClick,
  isLoading,
}: TableListProps<T>) {
  const localization = useLocalization(localizationSection)
  const componentsLocalization = useLocalization('components')
  const [sortProperty, setSortProperty] = useState('')
  const [sortDirection, setSortDirection] = useState<SortDirection>('none')
  const triggerSort = (property: string) => {
    if (property !== sortProperty || sortDirection === 'none') {
      setSortProperty(property)
      setSortDirection('ascending')
    } else if (sortDirection === 'ascending') setSortDirection('descending')
    else setSortDirection('none')
  }

  const [currentDisplayLimit, setCurrentDisplayLimit] =
    useState(baseDisplayLimit)

  const valueItems = useMemo(() => {
    const configs = configurations as Record<string, configuration>
    return items.map((item) => {
      const uniqueKey = getUniqueKey(item)
      const valueItem: Record<string, string> = {
        ...(item as unknown as Record<string, string>),
      }
      for (const fieldName in configs) {
        const configuration = configs[fieldName]
        valueItem[fieldName] = configuration.getValue
          ? configuration.getValue(valueItem[fieldName], uniqueKey)
          : valueItem[fieldName]
      }
      return { item, uniqueKey, valueItem }
    })
  }, [configurations, getUniqueKey, items])

  const sortedRows = useMemo(() => {
    const configuration = (configurations as Record<string, configuration>)[
      sortProperty
    ]
    if (sortDirection === 'none' || configuration == null) return valueItems
    else {
      return [...valueItems].sort((a, b) => {
        const aValue = a.valueItem[sortProperty]
        const bValue = b.valueItem[sortProperty]
        const sortResult = sortResultString(aValue, bValue)
        if (sortDirection === 'descending') return sortResult * -1
        return sortResult
      })
    }
  }, [configurations, sortProperty, sortDirection, valueItems])

  useEffect(() => {
    // reset display limit whenever we search to have as short a display list to render as possible

    const topOffset = document.getElementById('table-list')?.offsetTop ?? 0
    if (window.scrollY > topOffset) window.scrollTo({ top: topOffset })
    setTimeout(() => {
      setCurrentDisplayLimit(baseDisplayLimit)
    }, 1)
  }, [sortedRows, sortProperty, sortDirection])

  useEffect(() => {
    const pageDocument = document.documentElement
    if (pageDocument != null && sortedRows.length > currentDisplayLimit) {
      const scrollCheck = () => {
        if (
          pageDocument.scrollTop >
          pageDocument.scrollHeight -
            pageDocument.clientHeight -
            Math.max(window.innerHeight / 2, 250)
        ) {
          setCurrentDisplayLimit((p) => p + baseDisplayLimit)
        }
      }
      window.addEventListener('scroll', scrollCheck)
      return () => {
        window.removeEventListener('scroll', scrollCheck)
      }
    }
  }, [currentDisplayLimit, sortedRows])

  return (
    <div className="table-list" id="table-list">
      {isLoading ? (
        <div className="load-in-progress">
          <LoadingSpinner color="dark" />
          {componentsLocalization.text(
            'data-being-loaded',
            'De data word momenteel geladen.'
          )}
        </div>
      ) : (
        <>
          <div className="table-header">
            {Object.keys(configurations)
              .filter(
                (property) =>
                  (configurations as Record<string, configuration>)[property]
                    .show
              )
              .map((property) => (
                <div
                  key={property}
                  className={`column-${property}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    triggerSort(property)
                  }}
                >
                  {sortProperty === property && sortDirection !== 'none' ? (
                    <span className="sort-indicator">
                      {sortDirection === 'ascending' ? (
                        <FaIcon iconName="faSortUp" />
                      ) : (
                        <FaIcon iconName="faSortDown" />
                      )}
                    </span>
                  ) : (
                    <span className="sort-indicator sort-inactive">
                      <FaIcon iconName="faSort" />
                    </span>
                  )}
                  {localization.text(
                    property,
                    (configurations as Record<string, configuration>)[property]
                      .label
                  )}
                </div>
              ))}
          </div>
          {sortedRows.slice(0, currentDisplayLimit).map((row, index) => (
            <div
              className={`data-row ${index % 2 ? 'uneven' : 'even'} ${
                onRowClick ? 'clickable' : ''
              }`}
              key={row.uniqueKey}
              onClick={
                onRowClick
                  ? () => {
                      onRowClick(row.item)
                    }
                  : undefined
              }
            >
              {Object.keys(configurations)
                .filter(
                  (property) =>
                    (configurations as Record<string, configuration>)[property]
                      .show
                )
                .map((property) => {
                  const configuration = (
                    configurations as Record<string, configuration>
                  )[property]

                  return (
                    <div key={property} className={`column-${property}`}>
                      <span className="data-label">
                        {localization.text(property, configuration.label)}
                      </span>
                      <span className="data">
                        {configuration.getElementValue
                          ? configuration.getElementValue(
                              (row.item as Record<string, unknown>)[property],
                              row.uniqueKey
                            )
                          : row.valueItem[property]}
                      </span>
                    </div>
                  )
                })}
            </div>
          ))}
        </>
      )}
    </div>
  )
}
